import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getSortFieldForApi } from 'src/helpers/sort';
import { SurveyTimePeriodFilterValue } from 'src/modules/surveyResponses/types/SurveyTimePeriod';
import { useIntegrationService } from '../services';
import { GetHookDeliveriesParams, HookDelivery, HookDeliveryOutcome, Integration } from '../types';

const PAGE_SIZE = 25;
const DEFAULT_SORT = 'dateCreated_desc';
const ALL_TIME: SurveyTimePeriodFilterValue = {
  timePeriod: 'allTime',
  startDateInterval: null,
  endDateInterval: null,
};

type UseHookDeliveryLogControllerArgs = {
  hookId: string | null;
  companyId: string;
  onHookChanged: () => void;
};

export const useHookDeliveryLogController = ({
  hookId,
  companyId,
  onHookChanged,
}: UseHookDeliveryLogControllerArgs) => {
  const { getIntegration, getHookDeliveries, activateIntegration } = useIntegrationService();

  const [hook, setHook] = useState<Integration | null>(null);
  const [deliveries, setDeliveries] = useState<{ items: HookDelivery[]; count: number }>({
    items: [],
    count: 0,
  });
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(DEFAULT_SORT);
  const [outcome, setOutcome] = useState<HookDeliveryOutcome | ''>('');
  const [period, setPeriod] = useState<SurveyTimePeriodFilterValue>(ALL_TIME);
  const [isLoading, setIsLoading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const deliveriesRequestRef = useRef(0);
  // Changes on every open and close, so an answer started for an earlier opening of the same log is dropped
  const openingRef = useRef(0);

  useEffect(() => {
    openingRef.current += 1;
    setIsActivating(false);
    setHook(null);
    setDeliveries({ items: [], count: 0 });
    setPage(0);
    setSortField(DEFAULT_SORT);
    setOutcome('');
    setPeriod(ALL_TIME);
    if (!hookId || !companyId) {
      return;
    }

    let isCurrent = true;
    getIntegration(hookId, companyId)
      .then(result => {
        if (isCurrent) setHook(result);
      })
      .catch(() => {
        if (isCurrent) toast.error('Failed to load the webhook. Please try again.');
      });
    return () => {
      isCurrent = false;
    };
  }, [hookId, companyId, getIntegration]);

  const loadDeliveries = useCallback(async () => {
    const requestId = ++deliveriesRequestRef.current;
    if (!hookId || !companyId) {
      return;
    }

    const params: GetHookDeliveriesParams = {
      companyId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort: getSortFieldForApi(sortField) as GetHookDeliveriesParams['sort'],
      outcome: outcome || undefined,
      startDateInterval: period.startDateInterval ?? undefined,
      endDateInterval: period.endDateInterval ?? undefined,
    };

    setIsLoading(true);
    try {
      const result = await getHookDeliveries(hookId, params);
      if (requestId === deliveriesRequestRef.current) setDeliveries(result);
    } catch (e) {
      if (requestId !== deliveriesRequestRef.current) return;
      console.error(e);
      toast.error('Failed to load the delivery log. Please try again.');
      setDeliveries({ items: [], count: 0 });
    } finally {
      if (requestId === deliveriesRequestRef.current) setIsLoading(false);
    }
  }, [hookId, companyId, page, sortField, outcome, period, getHookDeliveries]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const activate = useCallback(async () => {
    if (!hookId || !companyId) {
      return;
    }

    const opening = openingRef.current;
    setIsActivating(true);
    try {
      const activated = await activateIntegration(hookId, companyId);
      toast.success('Webhook activated');
      onHookChanged();
      if (opening === openingRef.current) setHook(activated);
    } catch (e) {
      console.error(e);
      toast.error('Failed to activate the webhook. Please try again.');
    } finally {
      if (opening === openingRef.current) setIsActivating(false);
    }
  }, [hookId, companyId, activateIntegration, onHookChanged]);

  const changeSorting = useCallback((s: GridSortModel) => {
    setSortField(s[0] ? `${s[0].field}_${s[0].sort}` : DEFAULT_SORT);
    setPage(0);
  }, []);

  const changePagination = useCallback((p: GridPaginationModel) => {
    setPage(p.page);
  }, []);

  const changeOutcome = useCallback((value: HookDeliveryOutcome | '') => {
    setOutcome(value);
    setPage(0);
  }, []);

  const changePeriod = useCallback((value: SurveyTimePeriodFilterValue) => {
    setPeriod(value);
    setPage(0);
  }, []);

  return {
    hook,
    deliveries,
    page,
    pageSize: PAGE_SIZE,
    sortField,
    outcome,
    period,
    isLoading,
    isActivating,
    activate,
    changeSorting,
    changePagination,
    changeOutcome,
    changePeriod,
  };
};
